// Google applies the FIRST matching FeeRule (by SequenceNum). A later rule never
// applies when an earlier, still-active rule matches every itinerary it would match.

const today = () => new Date().toISOString().slice(0, 10);

const isExpired = (r, now) =>
  (r.travelDateDiscontinue && r.travelDateDiscontinue < now) ||
  (r.ticketingDateDiscontinue && r.ticketingDateDiscontinue < now);

const groupMembers = (groups, ref) => {
  const g = groups.find(x => `U:${x.tableNum}` === ref);
  return g ? (g.carriers || g.locations || []) : null;
};

// Does the earlier value `a` match everything the later value `b` matches?
function coversGrouped(a, b, groups) {
  if (!a) return true;
  if (!b) return false;
  if (a === b) return true;
  const aMembers = groupMembers(groups, a);
  if (!aMembers) return false;
  const bMembers = b.startsWith('U:') ? groupMembers(groups, b) : [b];
  return !!bMembers && bMembers.length > 0 && bMembers.every(m => aMembers.includes(m));
}

const coversCarrier = (a, b, carrierGroups) => a === '**' || coversGrouped(a, b, carrierGroups);
const coversExact = (a, b) => !a || a === b;
const coversPassenger = (a, b) => !a || a === 'ANY' || a === b;
const coversMin = (a, b) => a == null || (b != null && Number(b) >= Number(a));
const coversMax = (a, b) => a == null || (b != null && Number(b) <= Number(a));

const parsePrice = (p) => {
  const m = /^([A-Z]{3})?\s*(-?[\d.]+)$/.exec(String(p).trim());
  return m ? { cur: m[1] || '', val: parseFloat(m[2]) } : null;
};

function coversPrice(a, b) {
  if (!a.minPrice && !a.maxPrice) return true;
  const cur = (a.minPrice ? parsePrice(a.minPrice) : parsePrice(a.maxPrice))?.cur;
  const bCur = (b.minPrice ? parsePrice(b.minPrice) : b.maxPrice ? parsePrice(b.maxPrice) : null)?.cur;
  if (!bCur || bCur !== cur) return false;
  const aMin = a.minPrice ? parsePrice(a.minPrice).val : -Infinity;
  const aMax = a.maxPrice ? parsePrice(a.maxPrice).val : Infinity;
  const bMin = b.minPrice ? parsePrice(b.minPrice).val : -Infinity;
  const bMax = b.maxPrice ? parsePrice(b.maxPrice).val : Infinity;
  return bMin >= aMin && bMax <= aMax;
}

// ISO dates compare correctly as strings
const coversRange = (aStart, aEnd, bStart, bEnd) =>
  (!aStart || (bStart && bStart >= aStart)) && (!aEnd || (bEnd && bEnd <= aEnd));

export function ruleCovers(a, b, carrierGroups = [], locationGroups = []) {
  return coversCarrier(a.carrier, b.carrier, carrierGroups)
    && coversGrouped(a.origin, b.origin, locationGroups)
    && coversGrouped(a.destination, b.destination, locationGroups)
    && coversExact(a.tripType, b.tripType)
    && coversExact(a.roundTrip, b.roundTrip)
    && coversExact(a.cabinClass, b.cabinClass)
    && coversExact(a.cabinClassMatchMode, b.cabinClassMatchMode)
    && coversPassenger(a.passengerType, b.passengerType)
    && coversExact(a.priceSource, b.priceSource)
    && coversExact(a.interline, b.interline)
    && coversExact(a.sliceInterline, b.sliceInterline)
    && coversMin(a.minSegments, b.minSegments)
    && coversMin(a.minTotalSegments, b.minTotalSegments)
    && coversExact(a.layoverMinutes, b.layoverMinutes)
    && coversMax(a.maxHoursToDeparture, b.maxHoursToDeparture)
    && coversPrice(a, b)
    && coversRange(a.travelDateEffective, a.travelDateDiscontinue, b.travelDateEffective, b.travelDateDiscontinue)
    && coversRange(a.ticketingDateEffective, a.ticketingDateDiscontinue, b.ticketingDateEffective, b.ticketingDateDiscontinue);
}

export function describeRule(r) {
  const fee = r.suppress === 'true' ? 'SUPPRESS'
    : r.feePercent != null ? `${r.feePercent}%`
    : r.feeAmount ? r.feeAmount
    : r.oncePerBookingFeeAmount ? `once ${r.oncePerBookingFeeAmount}` : 'no fee';
  const parts = [
    `carrier=${r.carrier}`,
    `origin=${r.origin || 'ALL'}`,
    `dest=${r.destination || 'ALL'}`,
    r.tripType && `trip=${r.tripType}`,
    r.cabinClass && `cabin=${r.cabinClass}`,
    r.passengerType && `pax=${r.passengerType}`,
    r.priceSource && `source=${r.priceSource}`,
    (r.minPrice || r.maxPrice) && `price=${r.minPrice || '-'}..${r.maxPrice || '-'}`,
    (r.travelDateEffective || r.travelDateDiscontinue) && `travel=${r.travelDateEffective || '-'}..${r.travelDateDiscontinue || '-'}`,
    (r.ticketingDateEffective || r.ticketingDateDiscontinue) && `ticketing=${r.ticketingDateEffective || '-'}..${r.ticketingDateDiscontinue || '-'}`,
    `fee=${fee}`,
  ];
  return parts.filter(Boolean).join(' ');
}

// Returns rules that can never apply: blocked by an earlier rule, or expired
export function findNonApplyingRules(feeRules, carrierGroups = [], locationGroups = []) {
  const now = today();
  const sorted = [...feeRules].sort((a, b) => a.sequenceNum - b.sequenceNum);
  const blocked = [];
  const expired = [];
  sorted.forEach((rule, idx) => {
    if (isExpired(rule, now)) { expired.push(rule); return; }
    const blocker = sorted.slice(0, idx).find(prev => !isExpired(prev, now) && ruleCovers(prev, rule, carrierGroups, locationGroups));
    if (blocker) blocked.push({ rule, blockedBy: blocker });
  });
  return { blocked, expired };
}

// Plain-English reasons why `a` (earlier) catches everything `b` (later) would match
function explainCover(a, b, carrierGroups) {
  const reasons = [];
  if (a.carrier === '**') reasons.push('it applies to all carriers');
  else if (a.carrier !== b.carrier) {
    const members = groupMembers(carrierGroups, a.carrier) || [];
    reasons.push(b.carrier.startsWith('U:')
      ? `carrier group ${a.carrier} includes every carrier of ${b.carrier}`
      : `carrier group ${a.carrier} includes ${b.carrier} (${members.join(',')})`);
  }
  [['origin', 'origins'], ['destination', 'destinations']].forEach(([f, plural]) => {
    if (!a[f] && b[f]) reasons.push(`it has no ${f} limit (all ${plural}), so it also covers ${b[f]}`);
    else if (a[f] && a[f] !== b[f]) reasons.push(`${f} ${a[f]} includes ${b[f]}`);
  });
  if (!a.minPrice && !a.maxPrice && (b.minPrice || b.maxPrice)) reasons.push('it has no price limit');
  else if (a.minPrice || a.maxPrice) {
    if (a.minPrice !== b.minPrice || a.maxPrice !== b.maxPrice) reasons.push(`its price range ${a.minPrice || '-'}..${a.maxPrice || '-'} contains ${b.minPrice || '-'}..${b.maxPrice || '-'}`);
  }
  ['tripType', 'cabinClass', 'priceSource', 'interline', 'sliceInterline'].forEach(f => {
    if (!a[f] && b[f]) reasons.push(`it has no ${f} condition`);
  });
  if (reasons.length === 0) reasons.push('it has the same conditions');
  return reasons.join('; ');
}

const blockedFix = (rule, blockedBy) =>
  `Fix: move Rule #${rule.sequenceNum} above Rule #${blockedBy.sequenceNum}, narrow Rule #${blockedBy.sequenceNum}, or delete Rule #${rule.sequenceNum} if it is not needed.`;

// User-facing answer for "which rules are not applying?", built without the AI so it
// cannot invent results. `onlySeq` / `onlyCarrier` narrow the report.
export function nonApplyingRulesReport(feeRules, carrierGroups = [], locationGroups = [], { onlySeq, onlyCarrier } = {}) {
  let { blocked, expired } = findNonApplyingRules(feeRules, carrierGroups, locationGroups);
  const matches = (r) => (onlySeq == null || r.sequenceNum === onlySeq)
    && (!onlyCarrier || r.carrier === onlyCarrier || (groupMembers(carrierGroups, r.carrier) || []).includes(onlyCarrier));
  blocked = blocked.filter(b => matches(b.rule));
  expired = expired.filter(matches);

  if (onlySeq != null) {
    const rule = feeRules.find(r => r.sequenceNum === onlySeq);
    if (!rule) return `There is no Rule #${onlySeq} in the current file.`;
    if (blocked.length) {
      const { blockedBy } = blocked[0];
      return `Rule #${onlySeq} never applies.\n\nRule #${onlySeq}: ${describeRule(rule)}\nBlocked by Rule #${blockedBy.sequenceNum}: ${describeRule(blockedBy)}\n\nWhy: Google uses the first matching rule. Rule #${blockedBy.sequenceNum} comes first and matches every itinerary Rule #${onlySeq} would match, because ${explainCover(blockedBy, rule, carrierGroups)}.\n\n${blockedFix(rule, blockedBy)}`;
    }
    if (expired.length) return `Rule #${onlySeq} never applies because it has expired.\n\nRule #${onlySeq}: ${describeRule(rule)}\n\nFix: extend its discontinue date or delete it.`;
    return `Rule #${onlySeq} can apply. No earlier rule covers all of its itineraries, and it has not expired.\n\nRule #${onlySeq}: ${describeRule(rule)}\n\nNote: an earlier rule may still take part of its traffic (see Rule Health > conflicts).`;
  }

  const scope = onlyCarrier ? ` for ${onlyCarrier}` : '';
  if (!blocked.length && !expired.length) {
    return `All rules${scope} can apply. Checked ${feeRules.length} rules: none is fully blocked by an earlier rule and none has expired.`;
  }
  let text = `Google applies only the FIRST matching rule (by sequence number). Checked ${feeRules.length} rules${scope}:\n`;
  if (blocked.length) {
    text += `\n⛔ BLOCKED BY AN EARLIER RULE (${blocked.length})\n`;
    text += blocked.map(({ rule, blockedBy }) =>
      `\nRule #${rule.sequenceNum}: ${describeRule(rule)}\n  Blocked by Rule #${blockedBy.sequenceNum}: ${describeRule(blockedBy)}\n  Why: ${explainCover(blockedBy, rule, carrierGroups)}.\n  ${blockedFix(rule, blockedBy)}`
    ).join('\n');
  }
  if (expired.length) {
    text += `\n\n⌛ EXPIRED (${expired.length})\n`;
    text += expired.map(r => `\nRule #${r.sequenceNum}: ${describeRule(r)}`).join('');
    text += '\n\nFix: extend the discontinue dates or delete these rules.';
  }
  text += '\n\nRules that only partly overlap an earlier rule still apply to the remaining itineraries, so they are not listed here.';
  return text;
}

// Plain-text summary passed to the AI as authoritative context
export function nonApplyingRulesContext(feeRules, carrierGroups, locationGroups) {
  const { blocked, expired } = findNonApplyingRules(feeRules, carrierGroups, locationGroups);
  let text = `RULES THAT NEVER APPLY (computed by the system over all ${feeRules.length} rules - first match wins):\n`;
  if (blocked.length === 0 && expired.length === 0) return text + 'None. Every rule can match at least some itineraries.';
  if (blocked.length > 0) {
    text += `BLOCKED BY AN EARLIER RULE (${blocked.length}):\n`;
    text += blocked.map(({ rule, blockedBy }) =>
      `- Rule #${rule.sequenceNum} [${describeRule(rule)}] is blocked by Rule #${blockedBy.sequenceNum} [${describeRule(blockedBy)}]`
    ).join('\n');
  }
  if (expired.length > 0) {
    text += `\nEXPIRED (${expired.length}):\n`;
    text += expired.map(r => `- Rule #${r.sequenceNum} [${describeRule(r)}]`).join('\n');
  }
  return text;
}
